import { defineComponent, h } from 'vue'
import { QCheckbox, QField } from 'quasar'

import { bsComponentProps, useBsComponent } from '../useBsComponent'
import { useVm } from '../../../composables/useVm'
import { quasarHandlers } from '../quasarHandlers'
import { useTemplateRef } from '../useTemplateRef'

/**
 * A checkbox in a field shell, so a failed rule has somewhere to put its message.
 *
 * The `bsCheckbox` ref is load-bearing: `useBsComponent`'s auto-save looks for it to decide that the
 * clicked value is the new one and that the change check should be skipped — see the note there. It
 * cannot key off the component name, which the production build mangles.
 *
 * `required` means checked, so `isComponentValid` is `val === true`.
 */
export default defineComponent({
  name: 'BsCheckbox',

  // See BsInput: an undeclared DOM-named event is bound natively on the root as well, so the caller's
  // handler runs twice — the second time with a DOM event.
  emits: [ 'input', 'update:modelValue' ],

  /**
   * Off, so a caller's `label` reaches the QCheckbox only. With fallthrough on, Vue 3 passed it to the
   * QField root as a PROP as well, and QField drew it a second time as a small stacked label above
   * every box (Vue 2 left it as a harmless HTML attribute). The render hands the root everything else
   * fallthrough gave it, applied after its own props exactly as fallthrough was -- the arc wizard's
   * `tabindex="0"` is what makes its terms box focusable, and e2e focuses it.
   */
  inheritAttrs: false,

  props: { ...bsComponentProps },

  setup (props, { emit }) {
    // Template refs. A string `ref` in a render function does not populate `$refs` for a
    // component whose logic is in setup() -- see useTemplateRef.
    const { ref: bsCheckbox, set: setBsCheckboxRef } = useTemplateRef<any>()

    const vm = useVm()
    const bs = useBsComponent({
      vm,
      props,
      emit,
      autoSaveMethod: '',
      // Every click is a change; see the option's note in useBsComponent.
      autoSaveAlways: true,
      isComponentValid: (val?: any) => val === true
    })

    bs.forceTriggerOnChange.value = true

    return {
      bsCheckbox,
      setBsCheckboxRef,
      ...bs
    }
  },

  render (): any {
    const { label: _label, class: _class, ...fellThrough } = this.$attrs
    void _label

    return h(QField, {
      ref: this.setBsCheckboxRef,
      // so we get auto save classes applied.
      ...this.getValidationProps({
        borderless: true,
        bottomSlots: true,
        noErrorIcon: true,
        dense: true,
        stackLabel: true,
        hideBottomSpace: this.$attrs['hide-bottom-space'] !== void 0
      }),
      ...fellThrough,
      // Not a prop of ours, so it lands on the field's root, which is not a form control: Vue 3 writes
      // `disabled="false"` there where Vue 2 dropped it, and Quasar's `[disabled]` rule then greys the
      // box out and puts a not-allowed cursor on it.
      disabled: fellThrough.disabled === false ? void 0 : fellThrough.disabled,
      class: [_class, 'bs-checkbox', this.getClasses({
        'bs-checkbox--dense': this.$attrs.dense !== void 0
      })]
    }, {
        error: () => this.renderErrorSlot(),
        ...this.renderAutoSaveMessage(),
        default: () => [
      h(QCheckbox, {
        class: 'flex-start',
        ...this.getRenderProps({
          keepColor: true,
          color: this.$attrs.color || 'bs-g'
        }),
        ...quasarHandlers(this.getListeners()),
        ...this.getAttributes()
      }, this.getSlots())
    ]
      })
  }
})
